import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { shortDay } from '../record/format'
import { ACTIVITY_LABELS } from '../record/labels'
import { RECENT_TYPES } from './chips'
import type { RelationRef, TimelineItem } from './types'
import { itemKey, useTimeline } from './useTimeline'

/** One compact line: "Gesprek 'Kennismaking' · Jesse", "E-mailgesprek 'Pitch' · 3 berichten". */
function lineText(item: TimelineItem): string {
  switch (item.kind) {
    case 'activity': {
      const title = item.title ? ` '${item.title}'` : ''
      const author = item.author ? ` · ${item.author.name}` : ''
      return `${ACTIVITY_LABELS[item.type]}${title}${author}`
    }
    case 'mail_thread':
      return `E-mailgesprek${item.subject ? ` '${item.subject}'` : ''} · ${item.count} ${item.count === 1 ? 'bericht' : 'berichten'}`
    case 'task':
      return `Taak afgerond · ${item.title}`
    case 'system':
      return item.label
  }
}

/**
 * "Recente activiteit" on Overzicht: the five newest lines without system lines, with
 * "Alles →" to the tab Activiteit. On an organisation page the contact persons' lines come
 * along unless `includeMembers` is off.
 */
export function RecentActivity({
  relation,
  includeMembers,
  version,
  onShowAll,
}: {
  relation: RelationRef
  includeMembers?: boolean
  version: number
  onShowAll: () => void
}) {
  const members = includeMembers ?? true
  const { items, status } = useTimeline(relation, {
    types: RECENT_TYPES,
    q: '',
    includeMembers: members,
    limit: 5,
    version,
  })
  const heading =
    relation.kind === 'organisation' && members ? 'Recente activiteit · incl. contactpersonen' : 'Recente activiteit'

  return (
    <section aria-label="Recente activiteit" className="vonk-card flex flex-col gap-3 p-5">
      <header className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-base font-semibold">{heading}</h2>
        <Button type="button" variant="ghost" size="sm" onClick={onShowAll}>
          Alles →
        </Button>
      </header>
      {status === 'loading' ? (
        <div aria-busy="true" className="flex flex-col gap-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
      ) : status === 'error' ? (
        <p className="text-sm text-muted-foreground">Activiteit laden mislukt.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen activiteit.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {items.map((item) => (
            <li key={itemKey(item)} className="flex items-baseline gap-2 text-sm">
              <time dateTime={item.day} className="shrink-0 text-xs text-muted-foreground">
                {shortDay(item.day)}
              </time>
              <span className="min-w-0 flex-1 truncate">{lineText(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
